(() => {
  if (customElements.get('dl-page-ugc-wall-with-upload-m')) return;

  class DlPageUgcWallWithUpload extends HTMLElement {
    constructor() {
      super();
      this.activeModal = null;
      this.lastFocusedElement = null;
      this.previewRequestId = 0;
      this.modalLayerVersion = 0;
      this.handleClick = this.handleClick.bind(this);
      this.handleContextMenu = this.handleContextMenu.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.balanceColumns = this.balanceColumns.bind(this);
    }

    connectedCallback() {
      this.uploadModal = this.querySelector('[data-ugc-upload-modal]');
      this.previewModal = this.querySelector('[data-ugc-preview-modal]');
      this.previewImage = this.querySelector('[data-ugc-preview-image]');
      this.previewPetName = this.querySelector('[data-ugc-preview-pet-name]');
      this.wall = this.querySelector('.dl-page-ugc-wall-with-upload-m__wall');
      this.autoplay = this.dataset.ugcAutoplay !== 'false';
      this.columns = Array.from(this.querySelectorAll('.dl-page-ugc-wall-with-upload-m__column'));
      this.photos = Array.from(this.querySelectorAll('[data-ugc-photo]')).sort(
        (photoA, photoB) => Number(photoA.dataset.ugcIndex) - Number(photoB.dataset.ugcIndex)
      );
      this.shufflePhotos(this.photos);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('contextmenu', this.handleContextMenu);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      if (this.wall && this.columns.length === 3 && this.photos.length) {
        this.balanceColumns();
        if ('ResizeObserver' in window) {
          this.wallResizeObserver = new ResizeObserver(this.balanceColumns);
          this.wallResizeObserver.observe(this.wall);
        }

        if (!this.autoplay) return;

        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) {
          this.classList.add('is-ugc-visible');
        } else if ('IntersectionObserver' in window) {
          this.wallVisibilityObserver = new IntersectionObserver(
            ([entry]) => this.handleWallVisibility(entry.isIntersecting),
            { rootMargin: '600px 0px' }
          );
          this.wallVisibilityObserver.observe(this);
        } else {
          this.prepareMarqueeImages().then(() => this.classList.add('is-ugc-visible'));
        }
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('contextmenu', this.handleContextMenu);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.wallResizeObserver?.disconnect();
      this.wallVisibilityObserver?.disconnect();
      if (this.activeModal) this.unlockPage();
    }

    shufflePhotos(photos) {
      for (let index = photos.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [photos[index], photos[randomIndex]] = [photos[randomIndex], photos[index]];
      }
    }

    balanceColumns() {
      if (!this.columns?.length || !this.photos?.length) return;

      const columnWidth = this.columns[0].clientWidth;
      if (!columnWidth) return;
      if (Math.abs(columnWidth - (this.lastBalancedColumnWidth || 0)) < 0.5) return;
      this.lastBalancedColumnWidth = columnWidth;

      const gap = Number.parseFloat(getComputedStyle(this.wall).columnGap) || 0;
      const columnHeights = [0, 0, 0];
      const columnPhotos = this.columns.map(() => []);

      this.photos.forEach((photo) => {
        const image = photo.querySelector('img');
        const aspectRatio =
          Number.parseFloat(photo.dataset.aspectRatio) ||
          (image?.naturalWidth && image?.naturalHeight ? image.naturalWidth / image.naturalHeight : 1);
        const shortestColumnHeight = Math.min(...columnHeights);
        const shortestColumnIndex = columnHeights.indexOf(shortestColumnHeight);
        const estimatedHeight = columnWidth / aspectRatio;

        columnPhotos[shortestColumnIndex].push(photo);
        columnHeights[shortestColumnIndex] += estimatedHeight + gap;
      });

      this.columns.forEach((column, columnIndex) => {
        this.buildMarqueeColumn(column, columnPhotos[columnIndex], columnIndex, gap);
      });

      if (this.imagesPrepared) this.setMarqueeImagesEager();
    }

    handleWallVisibility(isIntersecting) {
      this.isWallIntersecting = isIntersecting;
      if (!isIntersecting) {
        this.classList.remove('is-ugc-visible');
        return;
      }

      this.prepareMarqueeImages().then(() => {
        if (this.isConnected && this.isWallIntersecting) this.classList.add('is-ugc-visible');
      });
    }

    prepareMarqueeImages() {
      if (this.imagesPrepared) return Promise.resolve();
      if (this.imagesPreparing) return this.imagesPreparing;

      const uniqueImages = this.setMarqueeImagesEager();

      this.imagesPreparing = Promise.all(
        uniqueImages.map((image) => {
          if (image.complete) {
            return typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
          }

          return new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          }).then(() => (typeof image.decode === 'function' ? image.decode().catch(() => {}) : undefined));
        })
      ).then(() => {
        this.imagesPrepared = true;
        this.imagesPreparing = null;
      });

      return this.imagesPreparing;
    }

    setMarqueeImagesEager() {
      const images = Array.from(
        this.querySelectorAll(
          '.dl-page-ugc-wall-with-upload-m__track > .dl-page-ugc-wall-with-upload-m__group:first-child img'
        )
      );
      const seenSources = new Set();
      const uniqueImages = images.filter((image) => {
        const source = image.currentSrc || image.src;
        if (!source || seenSources.has(source)) return false;
        seenSources.add(source);
        image.loading = 'eager';
        return true;
      });
      return uniqueImages;
    }

    buildMarqueeColumn(column, photos, columnIndex, gap) {
      column.replaceChildren();
      if (!photos.length) return;

      const track = document.createElement('div');
      const group = document.createElement('div');
      track.className = 'dl-page-ugc-wall-with-upload-m__track';
      track.dataset.direction = columnIndex === 1 ? 'down' : 'up';
      group.className = 'dl-page-ugc-wall-with-upload-m__group';

      photos.forEach((photo) => group.appendChild(photo));
      track.appendChild(group);
      column.appendChild(track);

      if (!this.autoplay) {
        track.classList.add('is-static');
        return;
      }

      let repeatCount = 0;
      while (group.scrollHeight < column.clientHeight + gap && repeatCount < 12) {
        photos.forEach((photo) => group.appendChild(this.clonePhoto(photo)));
        repeatCount += 1;
      }

      const clonedGroup = group.cloneNode(true);
      clonedGroup.setAttribute('aria-hidden', 'true');
      clonedGroup.querySelectorAll('button').forEach((button) => {
        button.tabIndex = -1;
      });
      track.appendChild(clonedGroup);

      const loopDistance = group.getBoundingClientRect().height + gap;
      const pixelsPerSecond = 20;
      const duration = loopDistance / pixelsPerSecond;
      track.style.setProperty('--ugc-loop-distance', `${loopDistance}px`);
      track.style.setProperty('--ugc-loop-duration', `${duration}s`);
      track.classList.add('is-animated');
    }

    clonePhoto(photo) {
      const clone = photo.cloneNode(true);
      clone.dataset.ugcClone = '';
      clone.tabIndex = -1;
      clone.setAttribute('aria-hidden', 'true');
      return clone;
    }

    handleClick(event) {
      const uploadTrigger = event.target.closest('[data-ugc-open-upload]');
      if (uploadTrigger) {
        this.openModal(this.uploadModal, uploadTrigger);
        return;
      }

      const photoTrigger = event.target.closest('[data-ugc-photo]');
      if (photoTrigger) {
        this.openPreview(photoTrigger);
        return;
      }

      if (event.target.closest('[data-ugc-close]')) {
        this.closeModal();
        return;
      }

      if (event.target === this.activeModal) this.closeModal();
    }

    handleContextMenu(event) {
      if (event.target.closest('[data-ugc-photo], [data-ugc-preview-image]')) event.preventDefault();
    }

    handleKeydown(event) {
      if (!this.activeModal) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (event.key === 'Tab') this.trapFocus(event);
    }

    handleBlockSelect(event) {
      if (this.contains(event.target)) this.openModal(this.uploadModal);
    }

    openPreview(photoTrigger) {
      if (!this.previewImage || !this.previewModal) return;

      const thumbnail = photoTrigger.querySelector('img');
      const thumbnailSrc = thumbnail?.currentSrc || thumbnail?.src || '';
      const fullSrc = photoTrigger.dataset.fullSrc || thumbnailSrc;
      const requestId = ++this.previewRequestId;

      this.previewImage.hidden = false;
      this.previewImage.alt = photoTrigger.dataset.alt || '';
      if (this.previewPetName) {
        const petName = photoTrigger.dataset.name?.trim() || '';
        this.previewPetName.textContent = petName;
        this.previewPetName.hidden = !petName;
      }
      if (thumbnail?.naturalWidth && thumbnail?.naturalHeight) {
        this.previewImage.width = thumbnail.naturalWidth;
        this.previewImage.height = thumbnail.naturalHeight;
      }
      if (thumbnailSrc) this.previewImage.src = thumbnailSrc;
      this.openModal(this.previewModal, photoTrigger);

      if (!fullSrc || fullSrc === thumbnailSrc) return;

      const fullImage = new Image();
      fullImage.src = fullSrc;

      const showFullImage = () => {
        if (
          requestId !== this.previewRequestId ||
          this.activeModal !== this.previewModal ||
          !fullImage.naturalWidth
        ) {
          return;
        }

        this.previewImage.src = fullSrc;
      };

      if (typeof fullImage.decode === 'function') {
        fullImage.decode().then(showFullImage).catch(() => {});
      } else {
        fullImage.addEventListener('load', showFullImage, { once: true });
      }
    }

    openModal(modal, trigger = null) {
      if (!modal) return;
      if (this.activeModal && this.activeModal !== modal) this.closeModal(false);

      this.modalLayerVersion += 1;
      this.classList.add('is-ugc-modal-active');
      this.activeModal = modal;
      this.lastFocusedElement = trigger || document.activeElement;
      modal.hidden = false;
      this.lockPage();
      requestAnimationFrame(() => {
        const initialFocus = modal.querySelector('[data-ugc-close]');
        if (initialFocus) initialFocus.focus({ preventScroll: true });
      });
      DLPixelH.capture('ugc_wall_pop_up_show', {
        action: modal.dataset.action,
        name: trigger.dataset.name,
        full_src: trigger.dataset.fullSrc
      });
    }

    closeModal(restoreFocus = true) {
      if (!this.activeModal) return;
      DLPixelH.capture('ugc_wall_pop_up_close', {
        action: this.activeModal.dataset.action,
        name: this.lastFocusedElement.dataset.name,
        full_src: this.lastFocusedElement.dataset.fullSrc
      });
      const closingModal = this.activeModal;
      const modalLayerVersion = ++this.modalLayerVersion;

      if (closingModal === this.previewModal && this.previewImage) {
        this.previewRequestId += 1;
        this.previewImage.hidden = true;
        this.previewImage.removeAttribute('src');
        this.previewImage.width = 1;
        this.previewImage.height = 1;
        this.previewImage.alt = '';
        if (this.previewPetName) {
          this.previewPetName.hidden = true;
          this.previewPetName.textContent = '';
        }
        this.previewImage.getBoundingClientRect();
      }

      closingModal.hidden = true;
      this.activeModal = null;
      this.unlockPage();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (modalLayerVersion === this.modalLayerVersion) {
            this.classList.remove('is-ugc-modal-active');
          }
        });
      });

      if (restoreFocus && this.lastFocusedElement?.isConnected) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
      this.lastFocusedElement = null;
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.activeModal.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockPage() {
      document.documentElement.classList.add('dl-ugc-modal-open');
      document.body.classList.add('dl-ugc-modal-open');
    }

    unlockPage() {
      document.documentElement.classList.remove('dl-ugc-modal-open');
      document.body.classList.remove('dl-ugc-modal-open');
    }
  }

  customElements.define('dl-page-ugc-wall-with-upload-m', DlPageUgcWallWithUpload);
})();
